import { ErrorModal } from "@dexteel/mesf-core";
import { Grid, Paper, Typography } from "@material-ui/core";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StrandTimeSeriesData } from "../caster/models/StrandSpeed";
import {
	getBilletTempByProcessId,
	getMoldLevelsByProcessId,
	getStrandSpeedsByProcessId,
} from "../caster/repositories/ProductionRepository";
import { Entry } from "../logbook/logbook-report/models/Entry";
import { HeaderHeatDataReport } from "./components/HeaderHeatDataReport";
import { HeatReportTabs } from "./components/tabs/HeatReportTabs";
import { useHeatReportContext } from "./context/HeatReportContext";
import {
	Addition,
	CasterProcessData,
	Charge,
	EAFProcessData,
	HeatData,
	HeatReportDelay,
	Heating,
	Probe,
	Production,
	ScrapData,
	StrandData,
} from "./models/HeatReportModels";
import {
	getCASProcessReport,
	getEAFProcessReport,
	getEAFValuesByProcessId,
	getHeatReport,
	getHeatReportChemistrySamples,
} from "./repository/HeatReportRepository";

export const HeatReportPage = () => {
	const { asset } = useParams<{ asset: "eaf" | "caster" | "lab" }>();
	const {
		state,
		state: { heat },
		actions: {
			setHeat,
			setEAFProcessData,
			setCASProcessData,
			setHeatLabData,
			setElementData,
			setAdditions,
			setCharges,
			setDelays,
			setLogbook,
			setProbes,
			setHeatings,
			setProductionData,
			setScrapData,
			setStrandData,
			setIsDataLoading,
			setStrandSpeeds,
			setMoldLevels,
			setEAFValues,
			setBilletTemp,
		},
	} = useHeatReportContext();

	const [error, setError] = useState<string>("");

	const getDataFromAPI = async (heatId: number | null) => {
		setIsDataLoading(true);
		const heatResp = await getHeatReport({ HeatId: heatId });
		if (heatResp.ok) {
			const heatData: HeatData = get(heatResp.data, "tables[0].rows[0]", {});
			setHeat(heatData);
		} else {
			setError(heatResp.message);
		}

		setIsDataLoading(false);
	};

	const getProcessDataFromAPI = async () => {
		setIsDataLoading(true);

		const processId = asset === "eaf" ? heat.EAFProcessId : heat.CASProcessId;

		if (asset === "eaf" && processId) {
			const resp = await getEAFProcessReport({
				ProcessId: processId,
			});
			if (resp.ok) {
				const processData: EAFProcessData = get(
					resp.data,
					"tables[0].rows[0]",
					{},
				);
				const additions: Addition[] = get(resp.data, "tables[1].rows", []);
				const probes: Probe[] = get(resp.data, "tables[2].rows", []);
				const charges: Charge[] = get(resp.data, "tables[3].rows", []);
				const heatings: Heating[] = get(resp.data, "tables[4].rows", []);
				const delays: HeatReportDelay[] = get(resp.data, "tables[5].rows", []);
				const logbook: Entry[] = get(resp.data, "tables[6].rows", []);

				setEAFProcessData(processData);
				setAdditions(additions);
				setProbes(probes);
				setCharges(charges);
				setHeatings(heatings);
				setDelays(delays);
				setLogbook(logbook);
			} else {
				setError(resp.message);
			}
			const eafValuesResp = await getEAFValuesByProcessId({
				ProcessId: processId,
			});
			if (eafValuesResp.ok) {
				const eafValues: StrandTimeSeriesData[] =
					eafValuesResp.data.tables[0].rows;
				setEAFValues(eafValues);
			} else {
				setError(eafValuesResp.message);
			}
		}
		if (asset === "caster" && processId) {
			const resp = await getCASProcessReport({
				ProcessId: processId,
			});
			if (resp.ok) {
				const processData: CasterProcessData = get(
					resp.data,
					"tables[0].rows[0]",
					{},
				);
				const strandData: StrandData[] = get(resp.data, "tables[1].rows", []);
				const probes: Probe[] = get(resp.data, "tables[2].rows", []);
				const productionData: Production[] = get(
					resp.data,
					"tables[3].rows",
					[],
				);
				const scrapData: ScrapData = get(resp.data, "tables[4].rows[0]", {});
				const logbook: Entry[] = get(resp.data, "tables[5].rows", []);

				setCASProcessData(processData);
				setStrandData(strandData);
				setScrapData(scrapData);
				setProductionData(productionData);
				setProbes(probes);
				setLogbook(logbook);
			} else {
				setError(resp.message);
			}

			const strandSpeedResp = await getStrandSpeedsByProcessId(processId);
			const moldLevelsResp = await getMoldLevelsByProcessId(processId);
			const billetTempResp = await getBilletTempByProcessId(processId);
			if (strandSpeedResp.ok) {
				const strandSpeeds: StrandTimeSeriesData[] =
					strandSpeedResp.data.tables[0].rows;
				setStrandSpeeds(strandSpeeds);
			} else {
				setError(strandSpeedResp.message);
			}
			if (moldLevelsResp.ok) {
				const moldLevels: StrandTimeSeriesData[] =
					moldLevelsResp.data.tables[0].rows;
				setMoldLevels(moldLevels);
			} else {
				setError(moldLevelsResp.message);
			}
			if (billetTempResp.ok) {
				const billetTemp: StrandTimeSeriesData[] =
					billetTempResp.data.tables[0].rows;
				setBilletTemp(billetTemp);
			} else {
				setError(billetTempResp.message);
			}
		}
		if (asset === "lab" && heat.HeatId) {
			const resp = await getHeatReportChemistrySamples({ heatId: heat.HeatId });
			if (resp.ok) {
				setHeatLabData(resp.data.tables[0].rows[0]);
				setElementData(resp.data.tables[1].rows);
			} else {
				setError(resp.message);
			}
		}
		setIsDataLoading(false);
	};

	useEffect(() => {
		(async () => {
			await getDataFromAPI(null);
		})();
	}, []);

	useEffect(() => {
		(async () => {
			await getProcessDataFromAPI();
		})();
	}, [asset, heat]);

	return (
		<Paper style={{ backgroundColor: " #F2F4F7", border: "1px solid  #ccc" }}>
			<Grid
				item
				xs={12}
				container
				spacing={1}
				justifyContent="center"
				alignContent="center"
			>
				<Grid item xs={12} style={{ margin: "15px 0px 0 20px" }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Heat Report
					</Typography>
				</Grid>
				<Grid item xs={12} container spacing={3}>
					<Grid item xs={12}>
						{heat && (
							<HeaderHeatDataReport
								heat={heat}
								getDataFromAPI={getDataFromAPI}
							/>
						)}
					</Grid>
				</Grid>
				<Grid
					item
					xs={12}
					container
					justifyContent="center"
					alignContent="center"
				>
					<HeatReportTabs />
				</Grid>
			</Grid>

			<ErrorModal error={error} onHide={() => setError("")} />
		</Paper>
	);
};
